import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addDetails, patchDetail, setBusinessType } from 'features/placeSlice';
import PropertyService from 'service/PropertyService';
import HostPlanPurchaseService from 'service/HostPlanPurchaseService';
import { notification } from 'antd';
import {
  OnboardingShell,
  Field,
  Group,
  TextInput,
  TextArea,
  SelectInput,
  RadioCard,
  RadioDot,
  Hint,
} from 'components/Onboarding';
import DOMPurify from 'dompurify';
import TiptapEditor from 'components/UI/TiptapEditor/TiptapEditor';
import { useOnboardingBackConfirm } from 'library/hooks/useOnboardingBackConfirm';
import { isAdminUser } from 'library/utils/isAdmin';

const MAX_TITLE_LENGTH = 50;
/** Plain-text limit (matches TipTap counter, not raw HTML byte length). */
const MAX_DESC_LENGTH = 5000;
const MAX_USPS_VIEW_LENGTH = 250;
/** Minimum plain-text length the host must write for the description. */
const MIN_DESC_LENGTH = 41;
const descTooShortMessage = `Use at least ${MIN_DESC_LENGTH} characters for the description.`;

/** Same notion of “length” as TiptapEditor (visible text, no HTML tags). */
function getPlainTextLengthFromHtml(html: string): number {
  if (!html || !String(html).trim()) return 0;
  if (typeof document !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent?.length ?? 0;
    } catch {
      // fall through
    }
  }
  return String(html).replace(/<[^>]*>/g, '').length;
}

const DEFAULT_DETAILS = {
  title: '',
  description: '',
  bnbUrl: '',
  property3dUrl: '',
  uspsAndView: '',
  houseRules: '',
  elderlyFriendly: '' as const,
  childFriendly: '' as const,
  petFriendly: '' as const,
  securityGuard24x7: '' as const,
  propertyStructureType: '' as const,
  poolLength: '',
  poolBreadth: '',
  poolDepth: '',
  parkingDescription: '',
  flexibleCheckinType: '' as const,
};

interface PropertyDetails {
  title: string;
  description: string;
  bnbUrl: string;
  property3dUrl: string;
  uspsAndView: string;
  houseRules: string;
  elderlyFriendly: '' | 'yes' | 'no';
  childFriendly: '' | 'yes' | 'no';
  petFriendly: '' | 'yes' | 'no';
  securityGuard24x7: '' | 'yes' | 'no';
  propertyStructureType: '' | 'standalone' | 'gated_complex';
  poolLength: string;
  poolBreadth: string;
  poolDepth: string;
  parkingDescription: string;
  flexibleCheckinType: '' | 'self' | 'assisted' | 'front_desk' | 'digital';
}

interface ErrorState {
  title?: string;
  description?: string;
  bnbUrl?: string;
  property3dUrl?: string;
  uspsAndView?: string;
  houseRules?: string;
}

const TitleAndDescription: React.FC = () => {
  // const modules = {
  //   toolbar: [
  //     [{ header: [1, 2, 3, 4, 5, 6, false] }],
  //     ["bold", "italic", "underline", "strike", "blockquote"],
  //     [{ size: [] }],
  //     [{ font: [] }],
  //     [{ align: ["right", "center", "justify"] }],
  //     // [{ list: "ordered" }, { list: "bullet" }],
  //     // ["link", "image"],
  //     [{ color: ["red", "#785412"] }],
  //     [{ background: ["red", "#785412"] }]
  //   ]
  // };

  const [text, setText] = useState('');
  const [textLength, setTextLength] = useState(0);
  const handleChange = (html: string, length: number): void => {
    const sanitizedValue = DOMPurify.sanitize(html);
    setText(sanitizedValue);
    setTextLength(length);
    setDetails((prev) => ({ ...prev, description: sanitizedValue }));
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [error, setError] = useState<ErrorState>({});
  const [details, setDetails] = useState<PropertyDetails>({ ...DEFAULT_DETAILS });
  const detail = useSelector((select: any) => select.detail);
  const businessType = useSelector((state: any) => state.businessType ?? 'B2C');
  const [businessTypeTouched, setBusinessTypeTouched] = useState(false);

  const isAdmin = isAdminUser();

  const useQuery = (): URLSearchParams => {
    return new URLSearchParams(useLocation().search);
  };
  let query = useQuery();
  const hostPropertyId = query.get('id');
  const isEdit =
    hostPropertyId !== 'null' &&
    hostPropertyId !== null &&
    hostPropertyId !== undefined
      ? true
      : false;

  useEffect(() => {
    const rawDesc = detail.description || '';
    const normalizedDesc =
      getPlainTextLengthFromHtml(rawDesc) === 0 ? '' : rawDesc;
    setDetails({ ...detail, description: normalizedDesc });
    setText(normalizedDesc);
    setTextLength(
      normalizedDesc ? getPlainTextLengthFromHtml(normalizedDesc) : 0,
    );
  }, [detail]);

  // Starter listings show guests generic copy, so the host's own title/description are locked.
  const [starterLocked, setStarterLocked] = useState(false);
  useEffect(() => {
    if (!isEdit || !hostPropertyId) return;
    HostPlanPurchaseService.isStarterCopy(hostPropertyId)
      .then(setStarterLocked)
      .catch(() => setStarterLocked(false));
  }, [isEdit, hostPropertyId]);

  // Self-hydrate when arriving directly at this screen in edit mode without Redux populated
  useEffect(() => {
    if (!isEdit || !hostPropertyId) return;
    if (detail?.title) return;

    const toYesNo = (v: unknown): '' | 'yes' | 'no' => {
      if (v === true || v === 'true' || v === 'yes' || v === 'YES') return 'yes';
      if (v === false || v === 'false' || v === 'no' || v === 'NO') return 'no';
      return '';
    };
    const toStructure = (v: unknown): '' | 'standalone' | 'gated_complex' => {
      const s = String(v || '').trim().toLowerCase().replace(/\s+/g, '_');
      if (s.includes('gated')) return 'gated_complex';
      if (s.includes('standalone')) return 'standalone';
      return '';
    };

    PropertyService.getPropertyDetailsById(hostPropertyId)
      .then((res) => {
        if ((res.responseInformation as any)?.responseCode !== 0) return;
        const d = res.result as any;
        const meta = d.propertyMetadata || {};
        dispatch(
          patchDetail({
            title: d.title || '',
            description: d.description || '',
            bnbUrl: d.bnbUrl || '',
            property3dUrl: d.property3dUrl || '',
            uspsAndView: meta.uspsAndView || '',
            houseRules: meta.houseRules || '',
            elderlyFriendly: toYesNo(meta.elderlyFriendly),
            childFriendly: toYesNo(meta.childFriendly),
            petFriendly: toYesNo(meta.petFriendly),
            securityGuard24x7: toYesNo(meta.securityGuard24x7),
            propertyStructureType: toStructure(meta.propertyStructureType),
          }),
        );
        if (d.businessType === 'B2B' || d.businessType === 'B2C') {
          dispatch(setBusinessType(d.businessType));
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, hostPropertyId, detail?.title]);

  const handleFieldChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ): void => {
    const { name, value } = e.target;
    const sanitizedValue = DOMPurify.sanitize(value);
    if (name) {
      setError((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    if (
      name === 'elderlyFriendly' ||
      name === 'childFriendly' ||
      name === 'petFriendly' ||
      name === 'securityGuard24x7'
    ) {
      setDetails((prev) => ({
        ...prev,
        [name]: (sanitizedValue as '' | 'yes' | 'no') || '',
      }));
      return;
    }

    if (name === 'propertyStructureType') {
      setDetails((prev) => ({
        ...prev,
        propertyStructureType: sanitizedValue as
          | ''
          | 'standalone'
          | 'gated_complex',
      }));
      return;
    }

    setDetails((prev) => ({ ...prev, [name]: sanitizedValue }));
  };

  const validate = (): boolean => {
    let newErrors: ErrorState = {};

    if (!details.title.trim()) {
      newErrors.title = 'Please enter title';
    }
    if (getPlainTextLengthFromHtml(details.description) === 0) {
      newErrors.description = 'Please enter description';
    }
    if (details.title.length > MAX_TITLE_LENGTH) {
      newErrors.title = `Max ${MAX_TITLE_LENGTH} characters allowed in the Title`;
    }
    const descPlainLen = getPlainTextLengthFromHtml(details.description);
    if (descPlainLen > 0 && descPlainLen < MIN_DESC_LENGTH) {
      newErrors.description = descTooShortMessage;
    }
    if (descPlainLen > MAX_DESC_LENGTH) {
      newErrors.description = `Max ${MAX_DESC_LENGTH} characters allowed in the Description`;
    }
    if (details.uspsAndView.length > MAX_USPS_VIEW_LENGTH) {
      newErrors.uspsAndView = `Max ${MAX_USPS_VIEW_LENGTH} characters allowed`;
    }
    const hasErrors = Object.keys(newErrors).length > 0;
    setError(hasErrors ? newErrors : {});
    return hasErrors;
  };

  const nextHandler = (): void => {
    if (validate()) {
      return;
    }
    dispatch(addDetails(details));
    if (isEdit && hostPropertyId) {
      // Persist businessType immediately for existing properties. Admin-only: the endpoint 403s for
      // hosts, and PropertyService's 403 handler signs the user out. Only when the admin actually
      // picked a value — the Redux value can be stale (persisted from a previously edited property).
      if (isAdmin && businessTypeTouched) {
        PropertyService.updateBusinessType(hostPropertyId, businessType).catch((err) => {
          console.error('Failed to update businessType', err);
          notification.error({ message: 'Could not save the property type. Please try again.' });
        });
      }
      navigate(`/host/location?id=${hostPropertyId}`);
      return;
    }
    navigate('/host/location');
  };

  const doBack = (): void => {
    dispatch(addDetails(details));
    if (isEdit) {
      navigate(`/host/description?id=${hostPropertyId}`);
      return;
    }
    navigate(`/host/description`);
  };

  const { backHandler, BackConfirmModal } = useOnboardingBackConfirm(doBack);

  // Shown as soon as the host starts typing, not only after pressing Continue.
  const liveDescLength = getPlainTextLengthFromHtml(details.description);
  const descriptionError =
    error.description ||
    (liveDescLength > 0 && liveDescLength < MIN_DESC_LENGTH ? descTooShortMessage : undefined);

  const pendingItems = [
    !details.title.trim() && 'Enter a listing title.',
    getPlainTextLengthFromHtml(details.description) === 0 && 'Enter a description.',
    getPlainTextLengthFromHtml(details.description) > 0 &&
      getPlainTextLengthFromHtml(details.description) < MIN_DESC_LENGTH &&
      descTooShortMessage,
  ].filter((item): item is string => typeof item === 'string');

  return (
    <OnboardingShell
      section={1}
      title="Describe your property for guests"
      onBack={backHandler}
      onContinue={nextHandler}
      continueDisabled={pendingItems.length > 0}
      pending={pendingItems}
      modal={BackConfirmModal}
    >
      {isAdmin && (
        <Group title="Property Type" note="B2C properties are visible to all customers. B2B properties are only visible to admin users.">
          {(['B2C', 'B2B'] as const).map((type) => (
            <RadioCard
              key={type}
              type="button"
              $active={businessType === type}
              onClick={() => {
                setBusinessTypeTouched(true);
                dispatch(setBusinessType(type));
              }}
            >
              <RadioDot $active={businessType === type} />
              {type === 'B2C' ? 'B2C (Consumer)' : 'B2B (Business)'}
            </RadioCard>
          ))}
        </Group>
      )}

      {starterLocked && (
        <Hint>
          Custom titles and descriptions are available on paid plans. On the Starter plan, guests see a
          generic title and description for your listing.
        </Hint>
      )}

      <Field label="Property listing title" required error={error.title}>
        <TextInput
          type="text"
          name="title"
          maxLength={50}
          title="Maximum 50 characters allowed"
          placeholder="Sea-facing villa with private pool"
          value={details.title}
          onChange={handleFieldChange}
          disabled={starterLocked}
          $invalid={!!error.title}
        />
        <Hint>{details.title.length}/50 characters</Hint>
      </Field>

      <Field label="Description" required error={descriptionError}>
        <EditorFrame $invalid={!!descriptionError}>
          <TiptapEditor
            editorId="property-description-editor"
            placeholder="Tell guests about the space, the surroundings and what makes a stay here special."
            value={text}
            onChange={handleChange}
            disabled={starterLocked}
          />
        </EditorFrame>
        <Hint>
          {textLength}/{MAX_DESC_LENGTH} characters
        </Hint>
      </Field>

      <Field label="What makes it special" info="Your property's USPs and the view from the property — listing features that make it stand out." error={error.uspsAndView}>
        <TextArea
          name="uspsAndView"
          rows={5}
          maxLength={MAX_USPS_VIEW_LENGTH}
          placeholder="Infinity pool overlooking the bay"
          value={details.uspsAndView}
          onChange={handleFieldChange}
          $invalid={!!error.uspsAndView}
        />
        <Hint>
          {details.uspsAndView.length}/{MAX_USPS_VIEW_LENGTH} characters
        </Hint>
      </Field>
    </OnboardingShell>
  );
};

const EditorFrame = styled.div<{ $invalid?: boolean }>`
  margin-top: 8px;
  border-radius: 12px;
  > div {
    border-color: ${({ $invalid }) => ($invalid ? '#be123c' : undefined)} !important;
  }
  .ProseMirror,
  .tiptap {
    font-family: inherit;
  }
  .tiptap p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    height: 0;
    color: #6b6b6b;
    opacity: 0.8;
    pointer-events: none;
  }
`;

export default TitleAndDescription;
